import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionProvider } from './context/PermissionContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import { DialogProvider } from './context/DialogContext';
import ToastContainer from './components/common/ToastContainer';
import ErrorBoundary from './components/common/ErrorBoundary';
import Layout from './components/layout/Layout';

// Lazy Load Pages
const Login = React.lazy(() => import('./pages/auth/Login'));
const Dashboard = React.lazy(() => import('./pages/dashboard/Dashboard'));
const TenantList = React.lazy(() => import('./pages/tenants/TenantList'));
const TenantForm = React.lazy(() => import('./pages/tenants/TenantForm'));
const PropertyList = React.lazy(() => import('./pages/properties/PropertyList'));
const PropertyForm = React.lazy(() => import('./pages/properties/PropertyForm'));
const ReceiptList = React.lazy(() => import('./pages/receipts/ReceiptList'));
const ReceiptForm = React.lazy(() => import('./pages/receipts/ReceiptForm'));
const ContractList = React.lazy(() => import('./pages/contracts/ContractList'));
const ContractForm = React.lazy(() => import('./pages/contracts/ContractForm'));
const RulesList = React.lazy(() => import('./pages/rules/RulesList'));
const RulesForm = React.lazy(() => import('./pages/rules/RulesForm'));
const ExpensesList = React.lazy(() => import('./pages/expenses/ExpensesList'));
const ExpensesForm = React.lazy(() => import('./pages/expenses/ExpensesForm'));
const UserForm = React.lazy(() => import('./pages/users/UserForm'));
const LocationManager = React.lazy(() => import('./pages/locations/LocationManager'));
const CompanySettings = React.lazy(() => import('./pages/settings/CompanySettings'));
const ContractSettings = React.lazy(() => import('./pages/settings/ContractSettings'));
const ReceiptSettings = React.lazy(() => import('./pages/settings/ReceiptSettings'));
const MapScreen = React.lazy(() => import('./pages/map/MapScreen'));
const NotificationList = React.lazy(() => import('./pages/notifications/NotificationList'));
const RoleList = React.lazy(() => import('./pages/roles/RoleList'));
const RoleForm = React.lazy(() => import('./pages/roles/RoleForm'));
const Profile = React.lazy(() => import('./pages/profile/Profile'));
const CompanyList = React.lazy(() => import('./pages/companies/CompanyList'));
const CompanyForm = React.lazy(() => import('./pages/companies/CompanyForm'));
const CompanyUsers = React.lazy(() => import('./pages/companies/CompanyUsers'));
const BranchList = React.lazy(() => import('./pages/branches/BranchList'));
const BranchForm = React.lazy(() => import('./pages/branches/BranchForm'));
const EmployeeList = React.lazy(() => import('./pages/employees/EmployeeList'));
const EmployeeProfile = React.lazy(() => import('./pages/employees/EmployeeProfile'));
const EmployeeForm = React.lazy(() => import('./pages/employees/EmployeeForm'));

// Payroll
const PayrollList = React.lazy(() => import('./pages/payroll/PayrollList'));
const PayrollRun = React.lazy(() => import('./pages/payroll/PayrollRun'));
const PayrollDetail = React.lazy(() => import('./pages/payroll/PayrollDetail'));

// Rent History
const RentHistory = React.lazy(() => import('./pages/rents/RentHistory'));
const TenantProfile = React.lazy(() => import('./pages/tenants/TenantProfile'));



// Loading Fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-full w-full min-h-[50vh]">
    <div className="inline-block w-8 h-8 border-4 border-primary-red/30 border-t-primary-red rounded-full animate-spin"></div>
  </div>
);

// Protected Route Component
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="inline-block w-8 h-8 border-4 border-primary-red/30 border-t-primary-red rounded-full animate-spin"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Main App Routes
const AppRoutes = () => {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingFallback /></div>}>
            <Login />
          </Suspense>
        }
      />
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/tenants" element={<TenantList />} />
                  <Route path="/tenants/new" element={<TenantForm />} />
                  <Route path="/tenants/:id/edit" element={<TenantForm />} />
                  <Route path="/properties" element={<PropertyList />} />
                  <Route path="/properties/new" element={<PropertyForm />} />
                  <Route path="/properties/:id/edit" element={<PropertyForm />} />
                  <Route path="/receipts" element={<ReceiptList />} />
                  <Route path="/receipts/new" element={<ReceiptForm />} />
                  <Route path="/receipts/:id/edit" element={<ReceiptForm />} />
                  <Route path="/contracts" element={<ContractList />} />
                  <Route path="/contracts/new" element={<ContractForm />} />
                  <Route path="/contracts/:id/edit" element={<ContractForm />} />
                  <Route path="/rules" element={<RulesList />} />
                  <Route path="/rules/new" element={<RulesForm />} />
                  <Route path="/rules/:id/edit" element={<RulesForm />} />
                  <Route path="/expenses" element={<ExpensesList />} />
                  <Route path="/expenses/new" element={<ExpensesForm />} />
                  <Route path="/expenses/:id/edit" element={<ExpensesForm />} />
                  <Route path="/users/new" element={<UserForm />} />
                  <Route path="/users/:id/edit" element={<UserForm />} />
                  <Route path="/locations" element={<LocationManager />} />
                  <Route path="/roles" element={<RoleList />} />
                  <Route path="/roles/new" element={<RoleForm />} />
                  <Route path="/roles/:id/edit" element={<RoleForm />} />
                  <Route path="/companies" element={<CompanyList />} />
                  <Route path="/companies/new" element={<CompanyForm />} />
                  <Route path="/companies/:id/edit" element={<CompanyForm />} />
                  <Route path="/companies/:id/users" element={<CompanyUsers />} />
                  <Route path="/branches" element={<BranchList />} />
                  <Route path="/branches/new" element={<BranchForm />} />
                  <Route path="/branches/:id/edit" element={<BranchForm />} />
                  <Route path="/settings" element={<CompanySettings />} />
                  <Route path="/settings/contract" element={<ContractSettings />} />
                  <Route path="/settings/receipt" element={<ReceiptSettings />} />
                  <Route path="/notifications" element={<NotificationList />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/map" element={<MapScreen />} />
                  <Route path="/employees" element={<EmployeeList />} />
                  <Route path="/employees/new" element={<EmployeeForm />} />
                  <Route path="/employees/:id/edit" element={<EmployeeForm />} />
                  <Route path="/employees/:id" element={<EmployeeProfile />} />

                  {/* Payroll Routes */}
                  <Route path="/payroll" element={<PayrollList />} />
                  <Route path="/payroll/new" element={<PayrollRun />} />
                  <Route path="/payroll/:id" element={<PayrollDetail />} />

                  {/* Rent Payment Routes */}
                  <Route path="/rent-history" element={<RentHistory />} />
                  <Route path="/tenants/:id" element={<TenantProfile />} />


                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Suspense>
            </Layout>
          </PrivateRoute>
        }
      />
    </Routes>
  );
};

const PermissionWrapper = ({ children }) => {
  const { user } = useAuth();
  return (
    <PermissionProvider permissions={user?.permissions || []}>
      {children}
    </PermissionProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <PermissionWrapper>
        <ThemeProvider>
          <ToastProvider>
            <DialogProvider>
              <BrowserRouter basename={process.env.PUBLIC_URL || ''}>
                <ErrorBoundary>
                  <AppRoutes />
                  <ToastContainer />
                </ErrorBoundary>
              </BrowserRouter>
            </DialogProvider>
          </ToastProvider>
        </ThemeProvider>
      </PermissionWrapper>
    </AuthProvider>
  );
}

export default App;

